import { useEffect, useState } from 'react'

import { faCode, faCodeBranch, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Loader from 'react-loaders'

import AnimatedLetters from '../AnimatedLetters'
import './index.scss'

// Projects data - easily scalable for future additions
const projects = [
  {
    id: 1,
    title: 'Project Assist',
    description: 'AI-powered project management and assistance platform built with Nuxt.js, integrating advanced AI capabilities to streamline project workflows and provide intelligent assistance.',
    technologies: ['Nuxt.js', 'Vue.js', 'AI', 'LLM', 'MCP', 'TypeScript', 'REST API'],
    category: 'Full Stack Development',
    status: 'Completed',
    year: '2025',
    company: 'Nagarro',
    features: [
      'AI-powered project assistance',
      'Nuxt.js based frontend',
      'Intelligent workflow automation',
      'Real-time project management'
    ],
    images: [],
    githubUrl: null,
    liveUrl: null,
    isPrivate: true
  },
  {
    id: 2,
    title: 'TOPS2',
    description: 'Real estate valuation and survey system built with Java Full Stack and Vue.js, enabling comprehensive property assessment and survey management.',
    technologies: ['Java', 'Spring Boot', 'Vue.js', 'MySQL', 'REST API', 'Microservices'],
    category: 'Full Stack Development',
    status: 'Completed',
    year: '2024',
    company: 'Nagarro',
    features: [
      'Real estate valuation system',
      'Property survey management',
      'Java Spring Boot backend',
      'Vue.js frontend interface'
    ],
    images: [],
    githubUrl: null,
    liveUrl: null,
    isPrivate: true
  },
  {
    id: 3,
    title: 'Connect My Partner (CMP)',
    description: 'CMP helps in requesting and managing remote access requests of partner companies. It makes partner access tracking and auditing simplified and can prevent companies\' network in case of an attack on any partner companies.',
    technologies: ['Java', 'Spring Boot', 'Angular 12', 'MySQL', 'REST API', 'Microservices'],
    category: 'Full Stack Development',
    status: 'Completed',
    year: '2022',
    company: 'Saint-Gobain',
    features: [
      'Remote access request management',
      'Partner access tracking and auditing',
      'Network security prevention',
      'Simplified access management workflow'
    ],
    images: [],
    githubUrl: null,
    liveUrl: null,
    isPrivate: true
  },
  {
    id: 4,
    title: 'Telecom Tools Suite',
    description: 'Comprehensive microservices-based project handling complete Telecom services of Saint-Gobain, including SIRENE (site information), TOM2 (order management), Delivery Organizer, SLA calculation, and TEMPO (invoice management).',
    technologies: ['Java', 'Spring Boot', 'Angular 12', 'MySQL', 'Microservices', 'REST API'],
    category: 'Microservices Architecture',
    status: 'Completed',
    year: '2021',
    company: 'Saint-Gobain',
    features: [
      'Complete telecom service management',
      'Microservices architecture',
      'Order and quotation management',
      'SLA calculation and validation',
      'Invoice checking and management'
    ],
    images: [],
    githubUrl: null,
    liveUrl: null,
    isPrivate: true
  }
]

const Projects = () => {
  const projectsArray = 'Projects'.split('')
  const [letterClass, setLetterClass] = useState('text-animate')
  const [filter, setFilter] = useState('All')

  useEffect(() => {
    const timer = setTimeout(() => {
      setLetterClass('text-animate-hover')
    }, 2000)
    return () => clearTimeout(timer)
  }, [])

  // Get unique categories for filtering
  const categories = ['All', ...new Set(projects.map(project => project.category))]
  
  // Add technology-based filters
  const technologyFilters = ['AI', 'NUXT']
  const allFilters = [...categories, ...technologyFilters]

  // Filter projects based on selected filter
  const filteredProjects = filter === 'All' 
    ? projects 
    : filter === 'AI'
    ? projects.filter(project => project.technologies.some(tech => tech.toLowerCase().includes('ai')))
    : filter === 'NUXT'
    ? projects.filter(project => project.technologies.some(tech => tech.toLowerCase().includes('nuxt')))
    : projects.filter(project => project.category === filter)

  return (
    <>
      <div className="container projects-page">
        <div className="text-zone">
          <h1>
            <AnimatedLetters
              letterClass={letterClass}
              strArray={projectsArray}
              idx={15}
            />
          </h1>
          <p>
            A showcase of my technical projects spanning various domains including
            Java Full Stack development, microservices architecture, AI integration, and 
            enterprise applications. Each project represents industry-grade solutions 
            delivered for telecom, finance, and real estate domains.
          </p>
          
          {/* Category and Technology Filter */}
          <div className="filter-container">
            {allFilters.map(filterOption => (
              <button
                key={filterOption}
                className={`filter-btn ${filter === filterOption ? 'active' : ''}`}
                onClick={() => setFilter(filterOption)}
              >
                {filterOption}
              </button>
            ))}
          </div>
        </div>

        <div className="projects-container">
          <div className="projects-grid">
            {filteredProjects.map((project, index) => (
              <div key={project.id} className="project-card" style={{animationDelay: `${index * 0.1}s`}}>
                {/* Project Images Section - with provision for multiple images */}
                {project.images && project.images.length > 0 && (
                  <div className="project-images">
                    <div className="image-carousel">
                      {project.images.map((image, idx) => (
                        <img key={idx} src={image} alt={`${project.title} ${idx + 1}`} />
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="project-content">
                  <div className="project-header">
                    <div className="project-meta">
                      <span className="category">{project.category}</span>
                      <span className="year">{project.year}</span>
                      <span className={`status ${project.status.toLowerCase()}`}>
                        {project.status}
                      </span>
                    </div>
                    <h3 className="project-title">{project.title}</h3>
                    <p className="company">{project.company}</p>
                  </div>

                  <p className="project-description">{project.description}</p>

                  <div className="project-features">
                    <h4>Key Features:</h4>
                    <ul>
                      {project.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="technologies">
                    {project.technologies.map((tech, idx) => (
                      <span key={idx} className="tech-tag">{tech}</span>
                    ))}
                  </div>

                  <div className="project-links">
                    {project.githubUrl && (
                      <a href={project.githubUrl} target="_blank" rel="noreferrer" className="project-link">
                        <FontAwesomeIcon icon={faCodeBranch} />
                        <span>Code</span>
                      </a>
                    )}
                    {project.liveUrl && (
                      <a href={project.liveUrl} target="_blank" rel="noreferrer" className="project-link">
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        <span>Live Demo</span>
                      </a>
                    )}
                    {project.isPrivate && (
                      <span className="private-indicator">
                        <FontAwesomeIcon icon={faCode} />
                        <span>Private Project</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Loader type="pacman" />
    </>
  )
}

export default Projects
